import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type {
  Connection,
  ConnectionListItem,
  ConnectionCreated,
  ConnectionTestResult,
  ConnectionRefreshResult,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  AuthorizeStarted,
  ConnectionDiagnostic,
  ConnectionDiagnosticsResponse,
  DiagnosticFilter,
} from "@/types";

const BASE = `${import.meta.env.VITE_API_URL}/connections`;

// READ — list (org-scoped server-side). The streaming endpoint serialises as a JSON array.
export function useConnections(searchTerm?: Ref<string>) {
  const { data, isFetching: connectionsLoading, error: connectionsError, refetch: refetchConnections } = useQuery({
    queryKey: computed(() => ["connections", searchTerm?.value ?? ""]),
    queryFn: () => {
      const term = searchTerm?.value?.trim();
      const qs = term ? `?searchTerm=${encodeURIComponent(term)}` : "";
      return fetchWrapper.get<ConnectionListItem[]>(`${BASE}${qs}`);
    },
  });
  const connections = computed(() => data.value ?? []);
  return { connections, connectionsLoading, connectionsError: connectionsError as Ref<ApiError | null>, refetchConnections };
}

// READ — single
export function useConnection(id: Ref<string>) {
  const { data: connection, isFetching: connectionLoading, error: connectionError } = useQuery({
    queryKey: computed(() => ["connection", id.value]),
    queryFn: () => fetchWrapper.get<Connection>(`${BASE}/${id.value}`),
    enabled: computed(() => !!id.value && id.value !== "new"),
  });
  return { connection, connectionLoading, connectionError: connectionError as Ref<ApiError | null> };
}

// READ — a connection's diagnostic events (newest-first, server-capped). Refetches when the
// filter changes; the filter is the raw query value the API parses (all | errors | setup | runtime).
export function useConnectionDiagnostics(id: Ref<string>, filter: Ref<DiagnosticFilter>) {
  const { data, isFetching: diagnosticsLoading, error: diagnosticsError, refetch: refetchDiagnostics } = useQuery({
    queryKey: computed(() => ["connection-diagnostics", id.value, filter.value]),
    queryFn: () => fetchWrapper.get<ConnectionDiagnosticsResponse>(
      `${BASE}/${id.value}/diagnostics?filter=${filter.value}`),
    enabled: computed(() => !!id.value && id.value !== "new"),
  });
  const diagnostics = computed<ConnectionDiagnostic[]>(() => data.value?.events ?? []);
  return { diagnostics, diagnosticsLoading, diagnosticsError: diagnosticsError as Ref<ApiError | null>, refetchDiagnostics };
}

export function useInvalidateConnections() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["connections"] });
}

// WRITE — create. Synchronous kinds land Active (setupCompleted=true); oauth2-authcode returns an
// authorizeUrl the caller redirects to; vendor-initiated returns a callbackState to round-trip.
export function useCreateConnection() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<ConnectionCreated, ApiError, CreateConnectionRequest>({
    mutationFn: (data) => fetchWrapper.post<ConnectionCreated>(BASE, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connections"] }),
  });
  return {
    createConnection: mutate,
    createConnectionAsync: mutateAsync,
    createConnectionPending: isPending,
    createConnectionError: error as Ref<ApiError | null>,
    createConnectionReset: reset,
  };
}

// WRITE — update non-secret fields (name / config / tags)
export function useUpdateConnection() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Connection, ApiError, { id: string; data: UpdateConnectionRequest }>({
    mutationFn: ({ id, data }) => fetchWrapper.put<Connection>(`${BASE}/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["connection", id] });
    },
  });
  return {
    updateConnection: mutate,
    updateConnectionAsync: mutateAsync,
    updateConnectionPending: isPending,
    updateConnectionError: error as Ref<ApiError | null>,
    updateConnectionReset: reset,
  };
}

// WRITE — rotate secrets (re-enter credential material)
export function useRotateConnection() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Connection, ApiError, { id: string; secrets: Record<string, string> }>({
    mutationFn: ({ id, secrets }) => fetchWrapper.post<Connection>(`${BASE}/${id}/rotate`, { secrets }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["connection", id] });
    },
  });
  return {
    rotateConnection: mutate,
    rotateConnectionAsync: mutateAsync,
    rotateConnectionPending: isPending,
    rotateConnectionError: error as Ref<ApiError | null>,
    rotateConnectionReset: reset,
  };
}

// WRITE — soft-delete (the "remove" action; hidden from reads, retained for audit)
export function useDeleteConnection() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, string>({
    mutationFn: (id) => fetchWrapper.delete(`${BASE}/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["connection", id] });
    },
  });
  return {
    deleteConnection: mutate,
    deleteConnectionAsync: mutateAsync,
    deleteConnectionPending: isPending,
    deleteConnectionError: error as Ref<ApiError | null>,
    deleteConnectionReset: reset,
  };
}

// WRITE — re-issue the authorize URL for a PendingSetup oauth2-authcode connection (resume).
export function useStartAuthorization() {
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<AuthorizeStarted, ApiError, string>({
    mutationFn: (id) => fetchWrapper.post<AuthorizeStarted>(`${BASE}/${id}/authorize`),
  });
  return {
    startAuthorization: mutate,
    startAuthorizationAsync: mutateAsync,
    startAuthorizationPending: isPending,
    startAuthorizationError: error as Ref<ApiError | null>,
    startAuthorizationReset: reset,
  };
}

// WRITE — connectivity test (inline result feedback)
export function useTestConnection() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<ConnectionTestResult, ApiError, string>({
    mutationFn: (id) => fetchWrapper.post<ConnectionTestResult>(`${BASE}/${id}/test`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["connection", id] });
      queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
  return {
    testConnection: mutate,
    testConnectionAsync: mutateAsync,
    testConnectionPending: isPending,
    testConnectionError: error as Ref<ApiError | null>,
    testConnectionReset: reset,
  };
}

// WRITE — user-initiated token refresh ("Refresh now", inline result feedback)
export function useRefreshConnection() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<ConnectionRefreshResult, ApiError, string>({
    mutationFn: (id) => fetchWrapper.post<ConnectionRefreshResult>(`${BASE}/${id}/refresh`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["connection", id] });
      queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
  return {
    refreshConnection: mutate,
    refreshConnectionAsync: mutateAsync,
    refreshConnectionPending: isPending,
    refreshConnectionError: error as Ref<ApiError | null>,
    refreshConnectionReset: reset,
  };
}
